import {
  DispatchCarryover,
  DispatchCarryoverPolicy,
  describeDispatchCarryoverPolicy,
  DispatchEntity,
  DispatchNotificationEntry,
  DispatchTrackingResult,
  Message,
  ObjectChangeLog,
} from "@ots-frontend/ots-data";
import {
  ArrowTopRightOnSquareIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  MapPinIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {IconArrowsSplit, IconCalendarOff} from "@tabler/icons-react";
import {ReactNode, useMemo} from "react";
import {AdminPanel} from "../../components/AdminPanel";
import {DescriptionItem, DescriptionList} from "../../components/DescriptionList";
import {OtsBadge} from "../../components/OtsBadge";
import {OtsActionButton} from "../../components/OtsButton";
import {OtsDateTime} from "../../components/OtsDateTime";
import {OtsEntityLink} from "../../components/OtsEntityLink";
import {OtsInlineAlert} from "../../components/OtsInlineAlert";
import {OtsSwitch} from "../../components/OtsSwitch";
import {OtsTimeline} from "../../components/OtsTimeline";
import {OtsTransportCategoryIcon} from "../../components/OtsTransportCategoryIcon";
import {StatusPill} from "../../components/StatusPill";
import {DispatchCarryoverBadge} from "../../dispatch/DispatchCarryoverBadge";
import {OtsBrand, OtsColorSchemePreference} from "../../theme/OtsTheme";
import {DriverTrackingPanel} from "../common/DriverTrackingPanel";
import {EntityChangeLog} from "../EntityProfile/EntityChangeLog";
import {EntityProfile, EntityProfileTab} from "../EntityProfile/EntityProfile";
import {DispatchActivityLog} from "./DispatchActivityLog";
import {DispatchCarryoverPanel} from "./DispatchCarryoverPanel";
import {
  buildDispatchActivity,
  getDispatchLifecycle,
  getDispatchStatusLabel,
  getDispatchStatusTone,
  getDispatchTitle,
  isDispatchClosed,
} from "./dispatch-profile-utils";

export type DispatchProfileTab = "overview" | "activity" | "change-log";

export type DispatchProfileProps = {
  dispatch: DispatchEntity;
  carryovers?: DispatchCarryover[];
  carryoversLoading?: boolean;
  messages?: Message[];
  messagesLoading?: boolean;
  messagesError?: boolean;
  /** Notification attempts sent to the driver (sent, skipped or failed). */
  notifications?: DispatchNotificationEntry[];
  /** The other dispatches in this dispatch's split group. */
  splits?: DispatchEntity[];
  tracking?: DispatchTrackingResult;
  trackingLoading?: boolean;
  trackingFetching?: boolean;
  trackingError?: unknown;
  trackingRefreshInterval?: number;
  changeLogs?: ObjectChangeLog[];
  changeLogsLoading?: boolean;
  /** Compact by default; dispatch screens are dense working views. */
  density?: "compact" | "default";
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  activeTab?: DispatchProfileTab;
  initialTab?: DispatchProfileTab;
  onTabChange?: (tab: DispatchProfileTab) => void;
  /** Each action shows only when the host supplies it, so hosts decide permissions. */
  onAddComment?: () => void;
  /** Sends the assigned driver a push notification; hidden for closed or unassigned dispatches. */
  onNotify?: () => void;
  onEdit?: () => void;
  onCancelDispatch?: () => void;
  onDelete?: () => void;
  onOpenTicket?: () => void;
  onOpenRequest?: () => void;
  onOpenDispatch?: (dispatchId: string) => void;
  onRefreshTracking?: () => void;
  /** Turns carryover off (true) or back on (false) for this dispatch; read-only when absent. */
  onCarryoverExcludedChange?: (excluded: boolean) => void;
  carryoverUpdating?: boolean;
  carryoverError?: string;
  /** The carryover settings that apply to this dispatch (driver's company, then region). */
  carryoverPolicy?: DispatchCarryoverPolicy;
  onClose?: () => void;
};

type LocationRef = {id?: string; name?: string};

const LocationCard = ({label, site, company, well, container}: {
  label: string;
  site: LocationRef;
  company: LocationRef;
  well: LocationRef;
  container: LocationRef;
}) => (
  <div className="flex min-w-0 gap-3 rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-background)] p-3">
    <MapPinIcon className="mt-0.5 size-5 shrink-0 text-[var(--ots-primary)]"/>
    <div className="min-w-0 text-xs">
      <p className="font-medium text-[var(--ots-text-muted)]">{label}</p>
      <p className="mt-1 text-sm font-semibold text-[var(--ots-secondary)]">
        <OtsEntityLink entityType="SITE" id={site.id} name={site.name} empty="Not specified"/>
      </p>
      {company.name && <p className="mt-0.5 text-[var(--ots-text-muted)]">
        <OtsEntityLink entityType="COMPANY" id={company.id} name={company.name}/>
      </p>}
      {(well.name || container.name) && <p className="mt-1 flex flex-wrap gap-x-2 text-[var(--ots-text-muted)]">
        {well.name && <span>Well <OtsEntityLink entityType="WELL" id={well.id} name={well.name}/></span>}
        {container.name && <span>Container <OtsEntityLink entityType="CONTAINER" id={container.id} name={container.name}/></span>}
      </p>}
    </div>
  </div>
);

const SectionHeading = ({children}: {children: ReactNode}) => (
  <h2 className="mb-3 text-base font-semibold text-[var(--ots-secondary)]">{children}</h2>
);

/** Tailwind dispatch profile: overview, activity log (comments, carryovers, status) and change log. */
export const DispatchProfile = ({
  dispatch,
  carryovers,
  carryoversLoading,
  messages,
  messagesLoading,
  messagesError,
  notifications,
  splits,
  tracking,
  trackingLoading,
  trackingFetching,
  trackingError,
  trackingRefreshInterval,
  changeLogs,
  changeLogsLoading,
  density = "compact",
  brand,
  colorScheme,
  activeTab,
  initialTab,
  onTabChange,
  onAddComment,
  onNotify,
  onEdit,
  onCancelDispatch,
  onDelete,
  onOpenTicket,
  onOpenRequest,
  onOpenDispatch,
  onRefreshTracking,
  onCarryoverExcludedChange,
  carryoverUpdating,
  carryoverError,
  carryoverPolicy,
  onClose,
}: DispatchProfileProps) => {
  const compact = density === "compact";
  const buttonSize = compact ? "compact" : "default";
  const closed = isDispatchClosed(dispatch);
  const activity = useMemo(
    () => buildDispatchActivity({dispatch, messages, carryovers, notifications}),
    [carryovers, dispatch, messages, notifications],
  );
  const otherSplits = (splits ?? []).filter(split => split.id !== dispatch.id);
  const request = dispatch.dispatchRequest;

  const overview = (
    <div className={["@container", compact ? "space-y-5" : "space-y-7"].join(" ")}>
      <DispatchCarryoverPanel
        carryoverCount={dispatch.carryoverCount}
        originalScheduleDttm={dispatch.originalScheduleDttm}
        lastCarryoverDttm={dispatch.lastCarryoverDttm}
        scheduleDttm={dispatch.scheduleDttm}
        history={carryovers}
        historyLoading={carryoversLoading}
        density={density}/>

      {request && (
        <OtsInlineAlert
          tone="info"
          size={compact ? "compact" : "default"}
          title="Linked to a dispatch request"
          action={onOpenRequest && <OtsActionButton size="compact" variant="text" icon={<ArrowTopRightOnSquareIcon/>} onClick={onOpenRequest}>Open request</OtsActionButton>}>
          <OtsEntityLink entityType="DISPATCHREQUEST" id={request.id} name={request.displayName}/>
          {request.externalId && ` · ${request.externalSystem ? `${request.externalSystem} ` : ""}${request.externalId}`}
        </OtsInlineAlert>
      )}

      <DescriptionList responsive="container" compact={compact}>
        <DescriptionItem label="Scheduled"><OtsDateTime value={dispatch.scheduleDttm}/></DescriptionItem>
        <DescriptionItem label="Driver"><OtsEntityLink entityType="PERSON" id={dispatch.personId} name={dispatch.personName} empty="Unassigned"/></DescriptionItem>
        <DescriptionItem label="Driver company"><OtsEntityLink entityType="COMPANY" id={dispatch.personCompanyId} name={dispatch.personCompanyName}/></DescriptionItem>
        <DescriptionItem label="Vehicle"><OtsEntityLink entityType="VEHICLE" id={dispatch.vehicleId} name={dispatch.vehicleName}/></DescriptionItem>
        <DescriptionItem label="Trailer"><OtsEntityLink entityType="TRAILER" id={dispatch.trailerId} name={dispatch.trailerName}/></DescriptionItem>
        <DescriptionItem label="Quantity">{dispatch.displayQuantity}</DescriptionItem>
        <DescriptionItem label="Freight type">{dispatch.freightTypeName}</DescriptionItem>
        <DescriptionItem label="Service type">{dispatch.pickupServiceTypeName}</DescriptionItem>
        <DescriptionItem label="Work order">{dispatch.workOrderNumber}</DescriptionItem>
        <DescriptionItem label="Internal work order">{dispatch.internalWorkOrderNumber}</DescriptionItem>
        <DescriptionItem label="Ticket number">{dispatch.ticketNumber}</DescriptionItem>
        <DescriptionItem label="Perform order">{dispatch.performOrder}</DescriptionItem>
      </DescriptionList>

      <section className="border-t border-[var(--ots-outline-variant)] pt-5">
        <SectionHeading>Route</SectionHeading>
        <div className="grid gap-3 @lg:grid-cols-2">
          <LocationCard
            label="Pickup"
            site={{id: dispatch.pickupSiteId, name: dispatch.pickupSiteName}}
            company={{id: dispatch.pickupSiteCompanyId, name: dispatch.pickupSiteCompanyName}}
            well={{id: dispatch.pickupWellId, name: dispatch.pickupWellName}}
            container={{id: dispatch.pickupContainerId, name: dispatch.pickupContainerName}}/>
          <LocationCard
            label="Delivery"
            site={{id: dispatch.deliverySiteId, name: dispatch.deliverySiteName}}
            company={{id: dispatch.deliverySiteCompanyId, name: dispatch.deliverySiteCompanyName}}
            well={{id: dispatch.deliveryWellId, name: dispatch.deliveryWellName}}
            container={{id: dispatch.deliveryContainerId, name: dispatch.deliveryContainerName}}/>
        </div>
      </section>

      {otherSplits.length > 0 && (
        <section className="border-t border-[var(--ots-outline-variant)] pt-5">
          <SectionHeading>Split loads</SectionHeading>
          <ul aria-label="Split loads" className="divide-y divide-[var(--ots-outline-variant)] rounded-md border border-[var(--ots-outline-variant)]">
            {otherSplits.map(split => (
              <li key={split.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="font-semibold text-[var(--ots-secondary)]">{split.displayQuantity}</p>
                  <p className="text-xs text-[var(--ots-text-muted)]">
                    <OtsEntityLink entityType="PERSON" id={split.personId} name={split.personName} empty="Unassigned"/> · <OtsDateTime value={split.scheduleDttm}/>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill tone={getDispatchStatusTone(split)}>{getDispatchStatusLabel(split)}</StatusPill>
                  {onOpenDispatch && <OtsActionButton size="compact" variant="text" onClick={() => onOpenDispatch(split.id)}>Open</OtsActionButton>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!closed && (
        <DriverTrackingPanel
          tracking={tracking}
          fallbackDestinationName={dispatch.pickupTransportId ? dispatch.deliverySiteName : dispatch.pickupSiteName}
          trailerName={dispatch.trailerName}
          loading={trackingLoading}
          fetching={trackingFetching}
          error={trackingError}
          refreshInterval={trackingRefreshInterval}
          onRefresh={onRefreshTracking}
          density={density}/>
      )}
    </div>
  );

  const commentCount = messages?.length;
  const tabs: EntityProfileTab<DispatchProfileTab>[] = [
    {id: "overview", label: "Overview", content: overview},
    {
      id: "activity",
      label: "Activity",
      count: commentCount,
      content: <DispatchActivityLog
        items={activity}
        loading={messagesLoading}
        error={messagesError}
        density={density}
        onAddComment={onAddComment}/>,
    },
    {
      id: "change-log",
      label: "Change log",
      count: changeLogs?.flatMap(log => log.objectFieldChangeList ?? []).length,
      content: <EntityChangeLog logs={changeLogs} loading={changeLogsLoading} density={density} ariaLabel="Dispatch change log"/>,
    },
  ];

  const lifecycle = getDispatchLifecycle(dispatch);
  const lastDone = lifecycle.map(event => event.done).lastIndexOf(true);

  return (
    <EntityProfile
      entityTypeLabel="Dispatches"
      title={getDispatchTitle(dispatch)}
      subtitle={<span>
        {dispatch.pickupSiteName ?? "Pickup not set"} → {dispatch.deliverySiteName ?? "Delivery not set"}
        {" · "}<OtsDateTime value={dispatch.scheduleDttm}/>
      </span>}
      status={<div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={getDispatchStatusTone(dispatch)}>{getDispatchStatusLabel(dispatch)}</StatusPill>
        <DispatchCarryoverBadge
          carryoverCount={dispatch.carryoverCount}
          originalScheduleDttm={dispatch.originalScheduleDttm}
          size={compact ? "compact" : "default"}/>
        {dispatch.category?.code && (
          <OtsBadge size={compact ? "compact" : "default"} icon={<OtsTransportCategoryIcon category={dispatch.category.code}/>}>
            {dispatch.category.code === "OIL" ? "Oil" : "Water"}
          </OtsBadge>
        )}
        {dispatch.splitGroupId && <OtsBadge size={compact ? "compact" : "default"} icon={<IconArrowsSplit/>}>Split load</OtsBadge>}
        {dispatch.carryoverExcluded && <OtsBadge size={compact ? "compact" : "default"} icon={<IconCalendarOff/>}>Carryover off</OtsBadge>}
      </div>}
      density={density}
      fullWidth
      brand={brand}
      colorScheme={colorScheme}
      tabs={tabs}
      activeTab={activeTab}
      initialTab={initialTab}
      onTabChange={onTabChange}
      actions={<>
        {onNotify && !closed && dispatch.personId && <OtsActionButton size={buttonSize} variant="secondary" icon={<BellAlertIcon/>} onClick={onNotify}>Notify driver</OtsActionButton>}
        {onAddComment && <OtsActionButton size={buttonSize} variant="secondary" icon={<ChatBubbleLeftRightIcon/>} onClick={onAddComment}>Add comment</OtsActionButton>}
        {onOpenTicket && dispatch.pickupTransportId && dispatch.deliveryTransportId && (
          <OtsActionButton size={buttonSize} variant="secondary" icon={<ArrowTopRightOnSquareIcon/>} onClick={onOpenTicket}>Ticket</OtsActionButton>
        )}
        {onEdit && <OtsActionButton size={buttonSize} variant="primary" icon={<PencilSquareIcon/>} onClick={onEdit}>Edit</OtsActionButton>}
        {onCancelDispatch && !closed && <OtsActionButton size={buttonSize} variant="secondary" icon={<NoSymbolIcon/>} onClick={onCancelDispatch}>Cancel dispatch</OtsActionButton>}
        {onDelete && <OtsActionButton size={buttonSize} variant="destructive" icon={<TrashIcon/>} onClick={onDelete}>Delete</OtsActionButton>}
        {onClose && <OtsActionButton size={buttonSize} variant="text" icon={<XMarkIcon/>} onClick={onClose}>Close</OtsActionButton>}
      </>}
      aside={<>
        <AdminPanel compact={compact} title="Status" bodyClassName={compact ? "p-4" : "p-5"}>
          <OtsTimeline
            ariaLabel="Dispatch status"
            density={density}
            items={lifecycle.map((event, index) => ({
              id: event.id,
              label: event.label,
              current: index === lastDone,
              tone: event.done ? event.tone : "neutral",
              description: event.dttm != null
                ? <OtsDateTime value={event.dttm}/>
                : event.done ? undefined : <span className="text-[var(--ots-text-muted)]">Not yet</span>,
            }))}/>
        </AdminPanel>
        <AdminPanel compact={compact} title="Carryover" bodyClassName={compact ? "p-4" : "p-5"}>
          <OtsSwitch
            size={compact ? "compact" : "default"}
            checked={!dispatch.carryoverExcluded}
            disabled={!onCarryoverExcludedChange || carryoverUpdating || closed}
            onCheckedChange={(checked) => onCarryoverExcludedChange?.(!checked)}
            label="Carry over when left open"
            description={closed
              ? "Closed dispatches don't carry over."
              : dispatch.carryoverExcluded
              ? "Stays on its scheduled day even if the driver is off shift."
              : carryoverPolicy
              ? "Moves to the next day if it's still open while the driver is off shift."
              : "Moves to the next day if it's still open while the driver is off shift, when carryover is on for this region or company."}/>
          {carryoverError && <p role="alert" className="mt-2 text-xs text-[var(--ots-error)]">{carryoverError}</p>}
          {carryoverPolicy && (
            <p className="mt-3 border-t border-[var(--ots-outline-variant)] pt-3 text-xs text-[var(--ots-text-muted)]">
              {describeDispatchCarryoverPolicy(carryoverPolicy)}
            </p>
          )}
        </AdminPanel>
        <AdminPanel compact={compact} title="Record" bodyClassName={compact ? "p-4" : "p-5"}>
          <DescriptionList className="!grid-cols-1 gap-y-4">
            <DescriptionItem label="Created"><OtsDateTime value={dispatch.createDttm}/></DescriptionItem>
            <DescriptionItem label="Created by"><OtsEntityLink entityType="PERSON" id={dispatch.createdById} name={dispatch.createByName}/></DescriptionItem>
            <DescriptionItem label="Last modified"><OtsDateTime value={dispatch.lastModifiedDttm}/></DescriptionItem>
            <DescriptionItem label="Dispatch ID"><span className="font-mono text-xs">{dispatch.id}</span></DescriptionItem>
          </DescriptionList>
        </AdminPanel>
      </>}/>
  );
};
